import axios from 'axios';
import { CaseStudies, Indicator, Habitats, Habitat, IndicatorsPostResponse, SpeciesV2 } from '../interfaces';

// const API_KEY = process.env.REACT_APP_PLANTNET_TOKEN;
const API_KEY = '2b10IIRdVxkhYtdx530XXHVIUe';
// const BASE_URL = process.env.REACT_APP_PLANTNET_URL;
const BASE_URL = 'https://my-api.plantnet.org';
// Documentaion: https://my-api.plantnet.org/

export const checkApiHealthPlanet = async () => {
  try {
    await axios.get(`${BASE_URL}/v2/guarden/cs?api-key=${API_KEY}`, {
      headers: {
        accept: 'application/json',
      },
    });
    return true;
  } catch (error) {
    return false;
  }
};

export const postGetPlantNetSpeciesPrediction = async (
  extent: any,
  exact_match: boolean,
  caseStudy: string,
  language: string,
): Promise<[SpeciesV2[], string | null]> => {
  try {
    const response = await axios.post<{
      species: any[];
      total: number;
    }>(
      `${BASE_URL}/v2/guarden/${caseStudy}/species/area?lang=${language}&api-key=${API_KEY}`,
      { extent, exact_match },
      {
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
        },
      },
    );

    const results = response.data.species || [];

    const speciesList: SpeciesV2[] = results.map((species: any) => ({
      id: species.gpn_id,
      name: species.name || undefined,
      author: species.author || undefined,
      family: species.family || undefined,
      commonNames: species.commonNames?.join(', ') || undefined,
      predictedName: species.predictedName || undefined,
      images: species.images?.map((image: any) => image.o) || [],
      taxaName: species.taxaName || undefined,
      taxaId: species.gbif_taxon_key?.toString() || undefined,
      taxaRank: species.taxaRank || undefined,
      taxaRankLevel: species.taxaRankLevel || undefined,
      category: species.is_tree ? 'tree' : undefined,
      is_invasive: species.is_invasive ? 'True' : 'False',
      is_endemic: undefined,
      is_eu_directive: species.is_eu_directive ? 'True' : 'False',
      is_grin_uses: species.is_grin_uses ? 'True' : 'False',
      iucn_red_list_category: species.iucn_red_list_category || undefined,
      latitude: species?.decimalLatitude || undefined,
      longitude: species?.decimalLongitude || undefined,
    }));

    return [speciesList, null];
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'An unexpected error occurred';
    return [[], errorMessage];
  }
};

// https://my-api.plantnet.org/#/DarwinCore/postV3DwcPlotSearch
export const postGetPlantnetPlotsSpeciesObservation = async (
  geometry: any,
  language: string,
): Promise<[SpeciesV2[], string | null]> => {
  try {
    const response = await axios.post<{
      results: any[];
      total: number;
    }>(
      `${BASE_URL}/v3/dwc/plot/search?api-key=${API_KEY}&lang=${language}`,
      JSON.stringify({ geometry, limit: 1000 }),
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );

    // Assuming the API returns { results: [...] }
    const results = response.data.results || [];

    const speciesList: SpeciesV2[] = results.flatMap((item) =>
      item.species.map((species: any) => ({
        id: item.id,
        name: species?.name || undefined,
        author: item.description?.match(/author:\s*"([^"]*)"/)?.[1] || undefined,
        family: species?.taxon?.family || undefined,
        commonNames: species?.taxon?.common_names?.en?.join(', ') || undefined,
        predictedName: item.predicted_name || undefined,
        images: item.media?.map((media: any) => media.large_url) || [],
        taxaName: species?.taxon?.name || undefined,
        taxaId: species?.taxon?.id || undefined,
        taxaRank: species?.taxon?.rank || undefined,
        taxaRankLevel: species?.taxon?.rank_level || undefined,
        category: species?.organs?.join(', ') || undefined,
        is_invasive: species?.is_invasive || undefined,
        is_endemic: undefined,
        is_eu_directive: species?.is_eu_directive || undefined,
        is_grin_uses: species?.is_grin_uses || undefined,
        iucn_red_list_category: species?.taxon?.iucn_red_list_category || undefined,
        observed_data: item?.observedOn ? new Date(item.observedOn) : undefined,
        latitude: item?.decimalLatitude || undefined,
        longitude: item?.decimalLongitude || undefined,
      })),
    );

    return [speciesList, null];
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'An unexpected error occurred';
    return [[], errorMessage];
  }
};

// https://my-api.plantnet.org/v3/dwc/occurrence/search
export const postGetPlantnetOccurrencesSpeciesObservation = async (
  geometry: any,
  language: string,
): Promise<[SpeciesV2[], string | null]> => {
  try {
    const response = await axios.post<{
      results: any[];
      total: number;
    }>(
      `${BASE_URL}/v3/dwc/occurrence/search?api-key=${API_KEY}&lang=${language}`,
      JSON.stringify({ geometry, limit: 1000 }),
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    );

    const results = response.data.results || [];

    const speciesList: SpeciesV2[] = results.map((species) => ({
      id: species.id,
      name: species.taxon?.name || undefined,
      author: species.scientificName?.match(/author:\s*"([^"]*)"/)?.[1] || undefined, // Adjust if author is elsewhere
      family: species.taxon?.family || undefined,
      commonNames: species.taxon?.common_names?.en?.join(', ') || undefined,
      predictedName: species.votes?.[0]?.determination || undefined, // Adjust based on your data
      images: species.media?.map((media: any) => media.large_url) || [],
      taxaName: species.taxon?.name || undefined,
      taxaId: species.taxon?.id || undefined,
      taxaRank: species.taxon?.rank || undefined,
      taxaRankLevel: species.taxon?.rank_level || undefined,
      category: species.media?.map((media: any) => media.organ).join(', ') || undefined, // Assuming organ is in media
      is_invasive: species.taxon?.is_invasive || undefined, // Adjust if available
      is_endemic: undefined, // Not in your sample data
      is_eu_directive: species.taxon?.is_eu_directive || undefined, // Adjust if available
      is_grin_uses: species.taxon?.is_grin_uses || undefined, // Adjust if available
      iucn_red_list_category: species.taxon?.iucn_red_list_category || undefined,
      observed_data: species?.observedOn ? new Date(species.observedOn) : undefined,
      latitude: species?.decimalLatitude || undefined,
      longitude: species?.decimalLongitude || undefined,
    }));

    return [speciesList, null];
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'An unexpected error occurred';
    return [[], errorMessage];
  }
};

export const fetchCaseStudies = async (): Promise<CaseStudies[]> => {
  try {
    const response = await axios.get<CaseStudies[]>(`${BASE_URL}/v2/guarden/cs?api-key=${API_KEY}`, {
      headers: {
        accept: 'application/json',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching garden data:', error);
    throw error;
  }
};

export const fetchHabitats = async (caseStudy: string): Promise<Habitat[]> => {
  try {
    const response = await axios.get<Habitats>(`${BASE_URL}/v2/guarden/${caseStudy}/habitats?api-key=${API_KEY}`, {
      headers: {
        accept: 'application/json',
      },
    });
    return response.data?.habitats;
  } catch (error) {
    console.error('Error fetching garden data:', error);
    throw error;
  }
};

export const fetchIndicatorsData = async (): Promise<Indicator[]> => {
  try {
    const response = await axios.get(`${BASE_URL}/v2/guarden/indicators?api-key=${API_KEY}`, {
      headers: {
        accept: 'application/json',
      },
    });
    return response.data.indicators;
  } catch (error) {
    console.error('Error fetching garden data:', error);
    throw error;
  }
};

export const postIndicatorsAreaData = async (
  extent: any,
  exact_match: boolean,
  code: any,
): Promise<IndicatorsPostResponse> => {
  try {
    const response = await axios.post<IndicatorsPostResponse>(
      `${BASE_URL}/v2/guarden/${code}/indicators/area?api-key=${API_KEY}`,
      { extent, exact_match },
      {
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching species data:', error);
    throw error;
  }
};
